import React from 'react';
import { Globe } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslations } from '../hooks/useTranslations';
import type { SupportedLanguage } from '../locales';
import { getArticleBySlug, getLocalizedBlogArticlePath } from '../blog/articles';
import {
  DUBBING_LANDING_PATH,
  getBasePathFromUrlPath,
  getBlogArticleSlugFromUrlPath,
  getLocalizedPath,
  isDubbingPairPath,
  type LocalizedBasePath,
} from '../utils/localizedRoutes';
import './Header.css';

export function Header() {
  const navigate = useNavigate();
  const { t, currentLanguage, availableLanguages } = useTranslations();
  const location = useLocation();
  const currentBasePath = getBasePathFromUrlPath(location.pathname);

  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
    pl: 'Polski',
    ja: '日本語',
    ko: '한국어',
    zh: '中文',
  };
  
  const isBlogPage = currentBasePath === '/blog';
  const isPairDubbingPage = Boolean(currentBasePath && isDubbingPairPath(currentBasePath));
  const isMainPage = currentBasePath === '/' || currentBasePath === DUBBING_LANDING_PATH || isPairDubbingPage;
  const isDubbingActive = currentBasePath === DUBBING_LANDING_PATH || isPairDubbingPage;
  
  const buildUrl = (path: LocalizedBasePath) => {
    const localizedPath = getLocalizedPath(path, currentLanguage);
    return localizedPath;
  };

  const handleLanguageChange = (newLanguage: string) => {
    if (newLanguage === currentLanguage) return;

    const targetLanguage = newLanguage as SupportedLanguage;
    const currentArticleSlug = getBlogArticleSlugFromUrlPath(location.pathname);

    if (currentArticleSlug) {
      const currentArticle = getArticleBySlug(currentArticleSlug, currentLanguage);
      if (currentArticle) {
        const localizedArticlePath =
          getLocalizedBlogArticlePath(currentArticle.id, targetLanguage);

        if (localizedArticlePath) {
          navigate(localizedArticlePath, { replace: true });
          return;
        }
      }
    }

    const basePath: LocalizedBasePath = currentBasePath || "/";
    const localizedPath = getLocalizedPath(basePath, targetLanguage);

    navigate(localizedPath, { replace: true });
  };

  return (
    <header className="vevi-header">
      <div className="vevi-header-container">
        <div className="vevi-header-content">
          <Link to={buildUrl("/")} className="vevi-header-logo" style={{ textDecoration: 'none', color: 'white' }}>
            VEVI Monster
          </Link>
          <nav className="vevi-header-nav">
            <Link
              to={buildUrl(DUBBING_LANDING_PATH)}
              className={`vevi-header-nav-link ${isDubbingActive ? "vevi-header-nav-link-active" : ""}`}
            >
              {t.services?.dubbing || 'Synchronisation'}
            </Link>
            <Link
              to={buildUrl("/blog")}
              className={`vevi-header-nav-link ${isBlogPage ? "vevi-header-nav-link-active" : ""}`}
            >
              {t.nav?.blog || 'Blog'}
            </Link>
          </nav>
          <div className="vevi-header-language">
            <Globe className="vevi-header-globe-icon" aria-hidden="true" />
            <label htmlFor="header-language-select" className="vevi-header-sr-only">
              Select language
            </label>
            <select
              id="header-language-select"
              className="vevi-header-select"
              value={currentLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              aria-label="Select language"
            >
              {availableLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {languageNames[lang] || lang.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </header>
  );
}
